import React, { useState } from 'react';
import { inject, observer } from 'mobx-react';
import { Button, Flex, Group, Modal, Notification, NumberInput, Select, Stack } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import * as z from 'zod';
import { t } from 'i18next';
import { IconCalendar, IconTestPipe } from '@tabler/icons-react';
import { CalendarDateInput } from '../../../global/component/molecules/CalendarDateInput';
import { ITestKitOrderLine } from '../../../global/entities/TestKitOrder/TestKitOrder.ts';
import { useGetTestKitOrderNew } from '../../../global/entities/TestKitOrder/queries/useGetTestKitOrderNew.tsx';

const initValues: ITestKitOrderLine = {
    testKitTypeID: 0,
    sampleNumber: '',
    trackingNumber: '',
    batchNumber: '',
    expirationDate: new Date(),
    price: 0,
    qty: 0,
};

export interface ITestKitModalProps {
    onAddRow: (newRow: ITestKitOrderLine) => void;
    opened: boolean;
    closeModal: () => void;
}

const TestKitModal: React.FC<ITestKitModalProps> = inject()(
    observer(({ onAddRow, opened, closeModal }) => {
        const [error, setError] = useState<string | null>(null);
        const { data: testKitOrderNew } = useGetTestKitOrderNew();

        const schema = z.object({
            testKitTypeID: z.number().min(1, { message: t('formError.testKitTypeRequired') }),
            expirationDate: z.date().refine((date) => !isNaN(date.getTime()), {
                message: t(t('formError.expirationDateRequired')),
            }),
            qty: z.number().min(1, { message: t('formError.qtyRequired') }),
        });

        const form = useForm<ITestKitOrderLine>({
            mode: 'controlled',
            initialValues: initValues,
            validate: zodResolver(schema),
        });

        const handleCancel = () => {
            form.reset();
            closeModal();
        };

        const handleSubmit = async (formValues: ITestKitOrderLine) => {
            const selectedTestKit = testKitOrderNew?.testKitTypes.find(
                (el) => el.id === Number(formValues.testKitTypeID),
            );
            const price = selectedTestKit?.price ?? 0;

            onAddRow({
                ...formValues,
                price,
            });
            closeModal();
        };

        return (
            <Modal opened={opened} onClose={closeModal} title={t('modal.testKitModal.title')}>
                <form onSubmit={form.onSubmit(handleSubmit)}>
                    <Stack gap={'md'}>
                        <Select
                            label={t('modal.testKitModal.testKitType')}
                            placeholder={t('modal.testKitModal.enterTestKitType')}
                            leftSection={<IconTestPipe size={16} />}
                            data={testKitOrderNew?.testKitTypesCombobox}
                            onChange={(value) => form.setFieldValue('testKitTypeID', Number(value))}
                            value={form.values.testKitTypeID > 0 ? form.values.testKitTypeID.toString() : null}
                            error={form.getInputProps('testKitTypeID').error}
                        />
                        <Group grow>
                            <CalendarDateInput
                                label={t('modal.testKitModal.expirationDate')}
                                placeholder={t('modal.testKitModal.enterExpirationDate')}
                                leftSection={<IconCalendar size={16} />}
                                valueFormat="YYYY-MM-DD"
                                {...form.getInputProps('expirationDate')}
                            />
                        </Group>

                        <Group grow>
                            <NumberInput
                                label={t('modal.testKitModal.quantity')}
                                placeholder={t('modal.testKitModal.enterQuantity')}
                                {...form.getInputProps('qty')}
                                maw={100}
                            />
                        </Group>

                        <Flex mt="md" justify={'flex-end'} gap={'md'}>
                            <Button variant="default" color="gray" onClick={handleCancel}>
                                {t('button.cancel')}
                            </Button>
                            <Button type="submit">{t('button.add')}</Button>
                        </Flex>

                        {error && (
                            <Notification color="red" onClose={() => setError(null)}>
                                {error}
                            </Notification>
                        )}
                    </Stack>
                </form>
            </Modal>
        );
    }),
);

export default TestKitModal;
